import { Link } from "@tanstack/react-router";

import { t } from "../i18n";
import { Checkbox, FieldMessage } from "./ui";

export interface TermsAcceptanceProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Inline validation error shown below the checkbox. Pass undefined to
   * hide. Convention: parent component shows this only after the user
   * has attempted an action without checking the box, NOT pre-emptively.
   */
  error?: string | undefined;
  disabled?: boolean;
}

/**
 * Checkbox for accepting Terms of Service + privacy notice on registration.
 *
 * Wraps the design-system `Checkbox` with:
 *   - i18n-driven label whose middle words are real links to /vilkar and
 *     /personvern (target="_blank" so users don't lose the form state when
 *     they go to read).
 *   - `FieldMessage` for the optional error — same component the rest of
 *     the form uses via `Field`, so the icon, colour and fade-in animation
 *     match every other field-level error on the page.
 *
 * Designed as a wrapper rather than inline JSX so the same component can
 * be dropped into future flows (e.g. re-acceptance after a ToS update, or
 * a settings screen) without re-implementing the link labels and error
 * styling each time. The legal copy lives in i18n; component callers don't
 * customise it.
 */
export function TermsAcceptance({ checked, onChange, error, disabled }: TermsAcceptanceProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        disabled={disabled}
        label={
          <span className="text-ink-700 text-sm leading-relaxed">
            {t.register.acceptanceIntro}{" "}
            <Link
              to="/vilkar"
              target="_blank"
              className="font-medium text-accent-700 underline-offset-4 hover:text-accent-900 hover:underline"
            >
              {t.register.acceptanceTermsLink}
            </Link>{" "}
            {t.register.acceptanceConnector}{" "}
            <Link
              to="/personvern"
              target="_blank"
              className="font-medium text-accent-700 underline-offset-4 hover:text-accent-900 hover:underline"
            >
              {t.register.acceptanceprivacyLink}
            </Link>
            .
          </span>
        }
      />
      <FieldMessage tone="error">{error}</FieldMessage>
    </div>
  );
}
